import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFeaturedMovies, typeLabel } from "@/utils/movieUtils";

export default function HeroCarousel() {
  const items = getFeaturedMovies();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (dir: number) => {
    setIndex((i) => (i + dir + items.length) % items.length);
  };

  return (
    <section className="relative h-[460px] w-full overflow-hidden md:h-[600px]">
      {items.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={movie.hero}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} 日本影视`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-7xl flex-col justify-end px-4 pb-14 md:px-8 md:pb-20">
        <div key={index} className="max-w-2xl animate-fade-up">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
              {typeLabel(items[index].type)}
            </span>
            <span className="rounded bg-accent-500 px-2 py-0.5 text-xs font-medium text-foreground-950">
              热播推荐
            </span>
            <span className="text-sm text-background-200">
              {items[index].year} · {items[index].region}
            </span>
            <span className="flex items-center gap-0.5 text-sm text-accent-400">
              <i className="ri-star-fill" aria-hidden="true"></i>
              {items[index].rating.toFixed(1)}
            </span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-background-50 md:text-5xl">
            {items[index].title}
          </h1>
          <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {items[index].synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${items[index].id}`}
              className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg" aria-hidden="true"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${items[index].type}`}
              className="flex items-center gap-2 rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 whitespace-nowrap"
            >
              更多{typeLabel(items[index].type)}
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(-1)}
        className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-background-50 backdrop-blur transition-colors hover:bg-black/50 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(1)}
        className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-background-50 backdrop-blur transition-colors hover:bg-black/50 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {items.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到 ${movie.title}`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all ${
              i === index ? "w-6 bg-primary-500" : "w-2 bg-background-50/60"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}